'use client'

import { useEffect, useRef } from 'react'
import { useAuth } from '@clerk/nextjs'
import { useQueryClient } from '@tanstack/react-query'

/**
 * Drops the previous user's cached data when the signed-in Clerk user changes.
 * Never on the first load (nothing → an id), so a fresh session keeps what it
 * has fetched. Keys carry the school, not the user, so without this one user's
 * cached data could render for the next user of the same tab.
 *
 * - Sign-out (an id → null): `clear()` empties the cache; the app navigates
 *   away, so no mounted screen needs telling.
 * - A different user (an id → another id): `resetQueries({ type: 'all' })` instead, because
 *   `clear()` does not notify mounted observers. Reset drops the data, tells
 *   every observer, and refetches the active queries as the new user.
 */
export function useClearQueryCacheOnUserChange(): void {
  const { isLoaded, userId } = useAuth()
  const queryClient = useQueryClient()
  const lastUserId = useRef<string | null>(null)

  useEffect(() => {
    if (!isLoaded) return
    const previous = lastUserId.current
    const current = userId ?? null
    lastUserId.current = current
    if (previous === null || previous === current) return
    if (current === null) queryClient.clear()
    else void queryClient.resetQueries({ type: 'all' })
  }, [isLoaded, userId, queryClient])
}
